import React from 'react';
import PropTypes from 'prop-types';
import TaskNumber from 'contents/ProtocolEditor/Components/TaskNumber';
import TaskTransferDescription from 'contents/ProtocolEditor/TaskList/Task/Components/TaskTransferDescription';
import '../../Task.scss';
import {useDispatch, useSelector} from 'react-redux';
import {getLabwareDataById} from 'stores/selectors/labwareStateSelectors';
import {getRuntimeStatus} from 'stores/selectors/runtimeSelector';
import {getDeepWellThreshold} from 'stores/selectors/configSelectors';
import Spinner from 'components/Spinner/Spinner';
import TaskExecutedTick from 'components/TaskExecutedTick';
import TaskWrapper from 'contents/ProtocolEditor/TaskList/Task/Components/TaskWrapper';
import TaskTitle from 'contents/ProtocolEditor/TaskList/Task/Components/TaskTitle';
import TaskAction from 'contents/ProtocolEditor/TaskList/Task/Components/TaskAction';
import TaskPipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/Task/Components/TaskPipettingProfileWrapper';
import {CardType, ExecutionStatus, RuntimeMode, TaskType} from 'constants/dataEnum';
import {getCardById} from 'stores/selectors/cardStateSelector';
import {getAspirateDispenseWellGroupName} from './simpleTransferTaskHelper';
import SimpleTransferPipettingProfile from './SimpleTransferPipettingProfile';
import {onEditSimpleTransferHandler} from './simpleTransferTaskBusiness';

function TaskTransfer({
  task,
  runtimeMode,
  readOnly,
  executionStatus,
  onShowMenuContext,
  onDelete,
  onCheckedChange,
  onSelectTask,
  cardId,
  // #region dragging business
  onDrag,
  isDragging,
  disableDrag,
  // #endRegion dragging business
}) {
  const dispatch = useDispatch();

  const labware = useSelector(getLabwareDataById(task.labwareID));
  const runtimeStatus = useSelector(getRuntimeStatus);
  const deepWellThreshold = useSelector(getDeepWellThreshold);
  const card = useSelector(getCardById(cardId));

  const {ID, volume, number, type, row, column, rowTipCount, columnTipCount, labwareIndex, isSelected, isIgnored} =
    task || {};

  const {name: labwareName, rowCount: labwareRowCount, colCount: labwareColumnCount} = labware || {};

  const wellGroupName = getAspirateDispenseWellGroupName(
    row,
    column,
    rowTipCount,
    columnTipCount,
    labwareRowCount,
    labwareColumnCount,
    labwareIndex,
  );

  const isDeepWell = labware.wellDepth > deepWellThreshold;

  const isShowPipettingProfile =
    !readOnly &&
    ((runtimeMode && runtimeStatus === RuntimeMode.PAUSED && executionStatus === ExecutionStatus.NONE) || !runtimeMode);

  const onEdit = () => {
    const callback = () => onSelectTask(null, {bypassValidityChecking: true});
    dispatch(onEditSimpleTransferHandler(task, cardId, callback));
  };

  return (
    <TaskWrapper
      disableDrag={disableDrag}
      onDrag={onDrag}
      isDragging={isDragging}
      isSelected={isSelected}
      onContextMenu={(e) => onShowMenuContext(e, task.ID, CardType.SIMPLE_TRANSFER, [task], cardId)}
      task={task}
      cardId={cardId}
    >
      <TaskNumber number={number} isHighlighted={isSelected} />
      <TaskTitle
        isDeepWell={isDeepWell}
        title={`${type} ${volume} μL`}
        description={
          <TaskTransferDescription
            labwareName={labwareName}
            wellGroup={wellGroupName}
            asDestination={type === TaskType.DISPENSE}
          />
        }
      />
      {!runtimeMode && !readOnly && (
        <TaskAction
          onEdit={onEdit}
          onDelete={onDelete}
          isIgnored={isIgnored}
          onIgnoreCheckboxChanged={(val) => onCheckedChange(ID, val)}
        />
      )}
      {isShowPipettingProfile && (
        <TaskPipettingProfileWrapper>
          <SimpleTransferPipettingProfile task={task} runtimeMode={runtimeMode} card={card} />
        </TaskPipettingProfileWrapper>
      )}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && <TaskExecutedTick />}
    </TaskWrapper>
  );
}

TaskTransfer.propTypes = {
  runtimeMode: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  onDrag: PropTypes.func.isRequired,
  disableDrag: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  onShowMenuContext: PropTypes.func.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  cardId: PropTypes.number,
  task: PropTypes.object.isRequired,
};

TaskTransfer.defaultProps = {
  cardId: null,
};

export default TaskTransfer;
